import { useMemo } from "react";
import useAllFields from "./useAllFields";
import useConfigurableFields from "./configurable-fields";
import { isFieldSettingEnabled } from "../utils/fieldSettings";

export default function useOrderedFields(fieldSettings, savedOrder) {
  const { identifiers, descriptiveInfo, warehouseInfo, originInfo, destinationInfo } = useAllFields();
  const { customFields = [] } = useConfigurableFields();

  const orderedFields = useMemo(() => {
    const enabled = fields =>
      fields.filter(field => isFieldSettingEnabled(fieldSettings, field.id));

    const allFields = {
      identifiers: enabled(
        identifiers.map(([id, cat, label]) => ({
          id,
          label,
          section: "identifiers",
        }))
      ),
      descriptive: enabled(
        descriptiveInfo.map(([id, cat, label]) => ({
          id,
          label,
          section: "descriptive",
        }))
      ),
      storage: enabled(
        warehouseInfo.map(([id, cat, label]) => ({
          id,
          label,
          section: "storage",
        }))
      ),
      origin: enabled(
        originInfo.map(([id, cat, label]) => ({
          id,
          label,
          section: "origin",
        }))
      ),
      destination: enabled(
        destinationInfo.map(([id, cat, label]) => ({
          id,
          label,
          section: "destination",
        }))
      ),
      configurable: customFields
        .filter(field => isFieldSettingEnabled(fieldSettings, `custom_field_${field.id}`))
        .map(field => ({
          id: `customField${field.id}`,
          section: "configurable",
          params: { field },
        })),
    };

    if (savedOrder?.version === 1 && savedOrder?.sections) {
      const newOrderedFields = {};
      const placedFieldIds = new Set();

      const findField = id => {
        for (const section of Object.values(allFields)) {
          const found = section.find(f => f.id === id);
          if (found) return found;
        }
        return null;
      };

      Object.keys(allFields).forEach(sectionId => {
        const savedSectionIds = savedOrder.sections[sectionId] || [];
        const sectionFields = [];

        savedSectionIds.forEach(id => {
          const field = findField(id);
          if (field && !placedFieldIds.has(id)) {
            sectionFields.push({ ...field, section: sectionId });
            placedFieldIds.add(id);
          }
        });

        const originalSection = allFields[sectionId] || [];
        originalSection.forEach(field => {
          if (!placedFieldIds.has(field.id)) {
            sectionFields.push(field);
            placedFieldIds.add(field.id);
          }
        });

        newOrderedFields[sectionId] = sectionFields;
      });

      return newOrderedFields;
    }

    return allFields;
  }, [
    fieldSettings,
    identifiers,
    descriptiveInfo,
    warehouseInfo,
    originInfo,
    destinationInfo,
    customFields,
    savedOrder,
  ]);

  return orderedFields;
}
